import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { secureStorage, migrateFromLocalStorage } from '../utils/security';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Migrate old localStorage tokens on first load
    migrateFromLocalStorage();

    const token = secureStorage.get('admin_token');
    const username = secureStorage.get('admin_username');
    const storeId = secureStorage.get('admin_store_id');
    const storeName = secureStorage.get('admin_store_name');
    const storeDomain = secureStorage.get('admin_store_domain');
    const role = secureStorage.get('admin_role');
    const adminPath = secureStorage.get('admin_path');
    
    if (token && username) {
      setUser({ 
        username, 
        token, 
        store_id: storeId || 'demo-store',
        store_name: storeName || 'Mi Tienda',
        store_domain: storeDomain || null,
        role: role || 'admin',
        admin_path: adminPath || null
      });
    }
    setLoading(false);
  }, []);

  const login = async (username, password, storeDomain = null) => {
    try {
      const response = await axios.post(`${API}/admin/login`, {
        username,
        password,
        store_domain: storeDomain
      });
      
      const { access_token, username: user_name, store_id, store_name, role, admin_path } = response.data;
      
      secureStorage.set('admin_token', access_token);
      secureStorage.set('admin_username', user_name);
      secureStorage.set('admin_store_id', store_id);
      secureStorage.set('admin_store_name', store_name);
      secureStorage.set('admin_store_domain', storeDomain || '');
      secureStorage.set('admin_role', role || 'admin');
      secureStorage.set('admin_path', admin_path || '');
      
      setUser({ 
        username: user_name, 
        token: access_token, 
        store_id, 
        store_name,
        store_domain: storeDomain,
        role: role || 'admin',
        admin_path: admin_path || null
      });
      return { success: true, admin_path };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Error de autenticación' 
      };
    }
  };

  const logout = useCallback(() => {
    secureStorage.remove('admin_token');
    secureStorage.remove('admin_username');
    secureStorage.remove('admin_store_id');
    secureStorage.remove('admin_store_name');
    secureStorage.remove('admin_store_domain');
    secureStorage.remove('admin_role');
    secureStorage.remove('admin_path');
    setUser(null);
  }, []);

  const getAuthHeader = useCallback(() => {
    // Read from state first, fall back to storage
    const token = user?.token || secureStorage.get('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [user]);

  const getStoreId = useCallback(() => {
    return user?.store_id || secureStorage.get('admin_store_id') || 'demo-store';
  }, [user]);

  const getStoreDomain = useCallback(() => {
    return user?.store_domain || secureStorage.get('admin_store_domain') || null;
  }, [user]);

  const getRole = useCallback(() => {
    return user?.role || secureStorage.get('admin_role') || 'admin';
  }, [user]);

  const getAdminPath = useCallback(() => {
    return user?.admin_path || secureStorage.get('admin_path') || null;
  }, [user]);

  const hasPermission = useCallback((requiredRoles) => {
    const role = user?.role || secureStorage.get('admin_role') || 'admin';
    return requiredRoles.includes(role);
  }, [user]);

  const getAdminBasePath = useCallback(() => {
    const adminPath = user?.admin_path || secureStorage.get('admin_path');
    if (adminPath) return `/${adminPath}`;
    const domain = user?.store_domain || secureStorage.get('admin_store_domain');
    return domain ? `/admin/${domain}` : '/admin';
  }, [user]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      logout, 
      getAuthHeader,
      getStoreId,
      getStoreDomain,
      getRole,
      getAdminPath,
      hasPermission,
      getAdminBasePath
    }}>
      {children}
    </AuthContext.Provider>
  );
};
